import { Button, Input, InputNumber, Select, Space, Tag, Table } from "antd";
import type { ColumnType, ColumnsType } from "antd/es/table";
import {
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { DebounceSelect, TableActionButtons } from "../../Components";
import type { SelectOption, VehicleOilRow, VehicleRow } from "./types";
import {
  getVehicleOwnershipLabel,
  getVehicleStatusLabel,
  VEHICLE_CONSUMPTION_FIELDS,
  VEHICLE_OWNERSHIP_OPTIONS,
  VEHICLE_STATUS_OPTIONS,
} from "./helpers";

type VehicleColumnOptions = {
  editingKey: string | null;
  canEdit: boolean;
  canDelete: boolean;
  hiddenColumns: string[];
  fetchTruckVendorOptions: (search: string) => Promise<SelectOption[]>;
  onEdit: (record: VehicleRow) => void;
  onSave: (record: VehicleRow) => void;
  isSaveLocked: (record: VehicleRow) => boolean;
  onCancel: () => void;
  onDelete: (record: VehicleRow) => void;
  onFieldChange: (rowKey: string, field: keyof VehicleRow, value: any) => void;
  onOilChange: (
    rowKey: string,
    region: string,
    field: keyof VehicleOilRow["consumption"],
    value: number | null,
  ) => void;
};

type VehicleTableColumn = ColumnType<VehicleRow> & {
  hidden?: boolean;
};

const isHidden = (hiddenColumns: string[], key: string) =>
  hiddenColumns.includes(key);

export const getVehicleColumns = ({
  editingKey,
  canEdit,
  canDelete,
  hiddenColumns,
  fetchTruckVendorOptions,
  onEdit,
  onSave,
  isSaveLocked,
  onCancel,
  onDelete,
  onFieldChange,
}: VehicleColumnOptions): ColumnsType<VehicleRow> => {
  const columns: VehicleTableColumn[] = [
    {
      title: "#",
      dataIndex: "_id",
      width: 56,
      fixed: "left",
      hidden: isHidden(hiddenColumns, "_id"),
      render: (_row: any, _record: VehicleRow, index: number) => index + 1,
    },
    {
      title: "Biển số xe",
      dataIndex: "licensePlate",
      width: 160,
      hidden: isHidden(hiddenColumns, "licensePlate"),
      render: (value: string, record: VehicleRow) =>
        editingKey === record.key ? (
          <Input
            value={value}
            onChange={(event) =>
              onFieldChange(record.key, "licensePlate", event.target.value)
            }
          />
        ) : (
          <strong>{value}</strong>
        ),
    },
    {
      title: "Nhà xe",
      dataIndex: "truckVendorName",
      width: 180,
      hidden: isHidden(hiddenColumns, "truckVendorName"),
      render: (_value: string, record: VehicleRow) =>
        editingKey === record.key ? (
          <DebounceSelect
            allowClear
            showSearch
            style={{ width: 150 }}
            value={record.truckVendor as any}
            fetchOptions={fetchTruckVendorOptions}
            onChange={(value) => onFieldChange(record.key, "truckVendor", value)}
          />
        ) : (
          record.truckVendorName
        ),
    },
    {
      title: "Hình thức sở hữu",
      dataIndex: "ownership",
      width: 170,
      hidden: isHidden(hiddenColumns, "ownership"),
      render: (value: string, record: VehicleRow) =>
        editingKey === record.key ? (
          <Select
            style={{ width: 130 }}
            value={value}
            options={VEHICLE_OWNERSHIP_OPTIONS}
            onChange={(nextValue) =>
              onFieldChange(record.key, "ownership", nextValue)
            }
          />
        ) : (
          getVehicleOwnershipLabel(value)
        ),
    },
    {
      title: "Dung tích bình dầu",
      dataIndex: "oilTankVolume",
      width: 160,
      hidden: isHidden(hiddenColumns, "oilTankVolume"),
      render: (value: number, record: VehicleRow) =>
        editingKey === record.key ? (
          <InputNumber
            min={0}
            value={value}
            onChange={(nextValue) =>
              onFieldChange(record.key, "oilTankVolume", nextValue ?? 0)
            }
          />
        ) : (
          value
        ),
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      width: 180,
      hidden: isHidden(hiddenColumns, "status"),
      render: (value: number, record: VehicleRow) =>
        editingKey === record.key ? (
          <Select
            style={{ width: 150 }}
            value={Number(value)}
            options={VEHICLE_STATUS_OPTIONS}
            onChange={(nextValue) =>
              onFieldChange(record.key, "status", nextValue)
            }
          />
        ) : (
          <Tag color={Number(value) === 1 ? "blue" : "red"}>
            {getVehicleStatusLabel(value)}
          </Tag>
        ),
    },
    {
      title: "Thao tác",
      dataIndex: "actions",
      width: 130,
      fixed: "right",
      render: (_value: any, record: VehicleRow) => {
        const saveLocked = isSaveLocked(record);

        return editingKey === record.key ? (
          <Space>
            <Button
              type="link"
              size="small"
              icon={<SaveOutlined />}
              loading={saveLocked}
              disabled={saveLocked}
              onClick={() => onSave(record)}
            >
              Lưu
            </Button>
            <Button
              type="link"
              size="small"
              icon={<CloseOutlined />}
              disabled={saveLocked}
              onClick={onCancel}
            >
              Hủy
            </Button>
          </Space>
        ) : (
          <TableActionButtons
            items={[
              {
                key: "edit",
                label: "Sửa",
                icon: <EditOutlined />,
                tone: "edit",
                hidden: !canEdit,
                onClick: () => onEdit(record),
              },
              {
                key: "delete",
                label: "Xóa",
                icon: <DeleteOutlined />,
                tone: "delete",
                hidden: !canDelete,
                onClick: () => onDelete(record),
              },
            ]}
          />
        );
      },
    },
  ];

  return columns.filter((column) => !column.hidden);
};

export const renderVehicleOilTable = (
  record: VehicleRow,
  editingKey: string | null,
  canEdit: boolean,
  onOilChange: VehicleColumnOptions["onOilChange"],
) => {
  const editable = canEdit && editingKey === record.key;
  const columns: ColumnsType<VehicleOilRow> = [
    {
      title: "Khu vực",
      dataIndex: "regionName",
      width: 140,
      fixed: "left",
      render: (value: string) => <strong>{value}</strong>,
    },
    ...VEHICLE_CONSUMPTION_FIELDS.map((field) => ({
      title: field.title,
      dataIndex: ["consumption", field.key],
      width: 150,
      render: (value: number, oilRow: VehicleOilRow) =>
        editable ? (
          <InputNumber
            min={0}
            value={value}
            onChange={(nextValue) =>
              onOilChange(record.key, oilRow.region, field.key, nextValue)
            }
          />
        ) : (
          value
        ),
    })),
  ];

  return (
    <Table
      size="small"
      rowKey="region"
      columns={columns}
      dataSource={record.oil}
      pagination={false}
      scroll={{ x: "max-content" }}
    />
  );
};
